import React, { useState } from 'react';
import '../styles/prompt-preview-modal.css';

interface GeneratedPrompt {
  sections: {
    [key: string]: string;
  };
  complete_prompt: string;
  generated_at: string;
}

interface PromptPreviewModalProps {
  prompt: GeneratedPrompt;
  promptType?: 'referral' | 'triage';
  onClose: () => void;
}

export const PromptPreviewModal: React.FC<PromptPreviewModalProps> = ({ prompt, promptType = 'referral', onClose }) => {
  const [activeTab, setActiveTab] = useState<'complete' | 'sections'>('complete');
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  
  const promptTypeLabel = promptType === 'triage' ? 'Triage' : 'Referral';

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(prompt.complete_prompt);
    setCopiedSection('complete');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const handleCopySection = (sectionName: string) => {
    const sectionContent = prompt.sections[sectionName];
    navigator.clipboard.writeText(sectionContent);
    setCopiedSection(sectionName);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const formatSectionName = (name: string) => {
    return name
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const sectionNames = Object.keys(prompt.sections);

  return (
    <>
      {/* Backdrop */}
      <div className="modal-backdrop" onClick={onClose}></div>

      {/* Modal */}
      <div className="prompt-preview-modal">
        <div className="modal-header">
          <h2>Generated {promptTypeLabel} Extraction Prompt</h2>
          <button 
            className="btn-close-modal"
            onClick={onClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Tabs */}
          <div className="modal-tabs">
            <button
              className={`tab-button ${activeTab === 'complete' ? 'active' : ''}`}
              onClick={() => setActiveTab('complete')}
            >
              Complete Prompt
            </button>
            <button
              className={`tab-button ${activeTab === 'sections' ? 'active' : ''}`}
              onClick={() => setActiveTab('sections')}
            >
              Sections ({sectionNames.length})
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'complete' && (
            <div className="tab-content">
              <div className="prompt-info-bar">
                <span className="info-text">
                  Generated: {new Date(prompt.generated_at).toLocaleString()}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleCopyPrompt}
                >
                  {copiedSection === 'complete' ? '✓ Copied!' : '📋 Copy Prompt'}
                </button>
              </div>
              <div className="prompt-display complete-prompt">
                <pre>{prompt.complete_prompt}</pre>
              </div>
            </div>
          )}

          {activeTab === 'sections' && (
            <div className="tab-content">
              <div className="sections-container">
                {sectionNames.map(sectionName => (
                  <div key={sectionName} className="section-item">
                    <div className="section-header">
                      <h4>{formatSectionName(sectionName)}</h4>
                      <button
                        className="btn btn-secondary btn-xs"
                        onClick={() => handleCopySection(sectionName)}
                      >
                        {copiedSection === sectionName ? '✓ Copied!' : 'Copy'}
                      </button>
                    </div>
                    <div className="section-content">
                      <pre>{prompt.sections[sectionName]}</pre>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};
